import { useTrendSeries } from '@liza/ui/hooks/useTrendSeries';
import { formatSleepDuration, isSleepDurationObservation, type ObservationOrigin } from '@liza/fhir-utils';
import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import { observationOriginLabel } from './observationOriginLabel';
import { resultVariantLabel } from './resultVariantLabel';
import { SeriesChart } from '../charts/SeriesChart';
import type { ChartConfig } from '../ui/chart';
import { ChartZoomToFitButton } from './ChartZoomToFitButton';
import { formatChartAxisMonth, formatChartDate, timeExtent } from './chartTime';

export interface CarePlanMeasurementChartProps {
  /** All readings for the measurement (sorted internally). */
  results: WithId<Observation>[];
  /** Y-axis unit label (e.g. "lbs", "mmHg"). */
  unit?: string;
  /** Line color (any CSS color); defaults to the primary token. */
  color?: string;
  /** Series labels for the tooltip (e.g. systolic / diastolic for blood pressure). */
  seriesLabels?: { y?: string; y2?: string };
  /**
   * Shared epoch-ms time domain across all charts in a topic's Results, so they align on one axis
   * instead of each scaling to its own range. Falls back to this measurement's own extent when absent.
   */
  domain?: [number, number];
  /** Ties this chart into a synchronized hover group with the other charts in the same Results. */
  syncId?: string;
  /**
   * The result code this measurement tracks. A row may hold readings coded a sibling of it (a
   * glucometer reading on a blood-glucose row, a clinic weight on a body-weight row), so the tooltip
   * names the variant when a point's own code differs from this one.
   */
  trackedCode?: string;
  className?: string;
}

/** The hovered point's row, as recharts hands it to a tooltip label formatter. */
type TooltipRow = {
  x?: number;
  provenanceOrigin?: ObservationOrigin;
  provenanceSource?: string;
  variantCode?: string;
  variantDisplay?: string;
};

/**
 * Large, labeled trend chart for a single measurement, shown in the CarePlan topic detail overlay.
 * A thin adapter over the shared `SeriesChart` — parses observations (imperial + composite values
 * via `toTrendSeries`) and renders a time-scale line, with the BP second series at reduced opacity.
 */
export function CarePlanMeasurementChart({
  results,
  unit,
  color = 'var(--color-primary)',
  seriesLabels,
  domain,
  syncId,
  trackedCode,
  className,
}: CarePlanMeasurementChartProps): React.ReactNode {
  const { t } = useTranslation('records');
  const { data, hasSecondLine } = useTrendSeries(results);

  // Read the date from the hovered point's payload rather than the tooltip `label` — the label is
  // derived from the x-axis, which is hidden here, so it would otherwise be empty.
  //
  // Provenance goes on its own line under the date: the writer's name when the reading carries one
  // ("Quest Diagnostics", "Apple Health"), and otherwise what kind of reading it is. Something is
  // always said, because this tooltip is the only thing explaining why a hand-typed reading's marker
  // is hollow. The line wraps rather than widening the tooltip, so a long practice name does not push
  // the rows below it apart.
  const formatTooltipHeader = useCallback(
    (_value: unknown, payload?: readonly { payload?: unknown }[]): React.ReactNode => {
      // We built these rows ourselves and handed them to the chart as `data`; recharts gives them back
      // untyped, so this is the one place that knows their shape.
      const row = payload?.[0]?.payload as TooltipRow | undefined;
      if (typeof row?.x !== 'number') return null;
      const provenance =
        row.provenanceSource ?? (row.provenanceOrigin && observationOriginLabel(t, row.provenanceOrigin));
      // Name the variant only when the reading carries a different code than the row tracks, so the
      // usual case stays a single clean line.
      const variant =
        row.variantCode && trackedCode && row.variantCode !== trackedCode
          ? resultVariantLabel(row.variantCode, row.variantDisplay)
          : undefined;
      const detail = [variant, provenance].filter(Boolean).join(' · ');
      return (
        <>
          <div>{formatChartDate(row.x)}</div>
          {detail && <div className="max-w-48 font-normal text-muted-foreground">{detail}</div>}
        </>
      );
    },
    [t, trackedCode],
  );

  if (data.length < 2) return null;

  // Flatten each point's provenance onto the row: the marker reads the origin, the tooltip the source.
  const rows = data.map((point) => ({
    ...point,
    provenanceOrigin: point.provenance?.origin,
    provenanceSource: point.provenance?.source,
    variantCode: point.code,
    variantDisplay: point.codeDisplay,
  }));

  // Sleep readings are minutes; format the axis/tooltip as "5h 30m" and drop the unit label (the
  // duration text carries the unit), rather than labeling a minutes axis "hours/night".
  const isSleep = results.some(isSleepDurationObservation);

  const config: ChartConfig = {
    // Sleep needs no series-name label — the "5h 30m" value speaks for itself. Otherwise fall back to the
    // unit so a single-value tooltip shows e.g. "lbs" instead of a blank label.
    y: { label: seriesLabels?.y ?? (isSleep ? '' : (unit ?? '')), color },
    // The only composite measurement is blood pressure, so fall back to "Diastolic" when the
    // observation's components carry no display name (rather than a blank second-series label).
    ...(hasSecondLine ? { y2: { label: seriesLabels?.y2 ?? 'Diastolic', color } } : {}),
  };

  // The readings' own time span — the zoom-to-fit control fits the timeline to this, regardless of the
  // shared `domain` the chart is currently painted on.
  const dataExtent = timeExtent(data.map((p) => p.x));

  return (
    <div className={cn('relative', className)}>
      <ChartZoomToFitButton range={dataExtent} className="absolute top-0 right-1 z-10" />
      <SeriesChart
        data={rows}
        config={config}
        className="h-[140px]"
        dotsOnly
        hideXAxis
        secondSeriesOpacity={0.5}
        syncId={syncId}
        syncMethod="value"
        xAxis={{
          type: 'number',
          scale: 'time',
          domain: domain ?? ['dataMin', 'dataMax'],
          tickFormatter: formatChartAxisMonth,
        }}
        yAxis={{
          domain: ['dataMin', 'dataMax'],
          label: isSleep ? undefined : unit,
          tickFormatter: isSleep ? formatSleepDuration : undefined,
        }}
        valueFormatter={isSleep ? formatSleepDuration : undefined}
        tooltipLabelFormatter={formatTooltipHeader}
      />
    </div>
  );
}
